import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import { ICON_SIZES, SOURCES_DEFAULT_ICON } from "src/helpers/constants";
import { MdClose, MdEdit, MdOutlineShare } from "react-icons/md";
import { useMemo } from "react";
import useAllTransactions from "src/hooks/useAllTransactions";
import { rgbaObjectToString } from "src/helpers/utils";
import clsx from "clsx";
import Icon from "../Icon/Icon";
import useMobile from "src/hooks/useMobile";
import { Link, useNavigate } from "react-router-dom";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import SourcesContactCards from "src/pages/Sources/SourcesContactCards";
import { motion } from "framer-motion";
import { EButtonKind } from "../Button/ButtonTypes";

interface IClientProviderInfoModal {
  onClose: () => void;
  clientProvider: string;
}

const ClientProviderInfoModal: React.FC<IClientProviderInfoModal> = ({
  onClose,
  clientProvider,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const { getSourceCustomSettings, isLoadingCustomizedSources } =
    useAllTransactions();

  const { icon, picture, iconStyles, contactData, address } = useMemo(() => {
    const res = getSourceCustomSettings(clientProvider);

    return {
      icon: res?.icon || SOURCES_DEFAULT_ICON,
      picture: res?.picture || null,
      iconStyles: {
        background: rgbaObjectToString(res?.bg),
        color: rgbaObjectToString(res?.color),
      },
      address: res?.address
        ? Array.isArray(res?.address)
          ? ""
          : res?.address
        : "",
      contactData: res?.contact || [],
    };
  }, [getSourceCustomSettings, clientProvider, isLoadingCustomizedSources]);

  return (
    <Modal
      onEscape={() => onClose()}
      className={clsx(["md:!max-w-[450px]", "md:!w-full"])}
    >
      <Modal.Header title="Información de contacto" onClose={() => onClose()} />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
          <div className={clsx(["flex", "flex-row", "gap-4"])}>
            <motion.div
              className={clsx([
                "flex",
                "items-center",
                "justify-start",
                "!min-h-[64px]",
                "md:!min-h-[96px]",
                "!max-w-[64px]",
                "!min-w-[64px]",
                "md:!max-w-[96px]",
                "md:!min-w-[96px]",
              ])}
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }} // cuadrado pequeño
              animate={{ opacity: 1 }} // completamente visible
              transition={{ duration: 0.5, ease: "easeInOut" }}
            >
              {picture?.thumbnailUrl ? (
                <img
                  src={picture.thumbnailUrl}
                  className={clsx([
                    "rounded-md",
                    "!min-h-[64px]",
                    "!max-w-[64px]",
                    "!min-w-[64px]",
                    "md:!min-h-[96px]",
                    "md:!max-w-[96px]",
                    "md:!min-w-[96px]",
                    "bg-checkers",
                  ])}
                />
              ) : (
                <Icon
                  name={icon}
                  size={isMobile ? ICON_SIZES.XL : ICON_SIZES.XL * 1.5}
                  className={clsx(["rounded-md", "p-2", "md:p-2"])}
                  style={iconStyles}
                />
              )}
            </motion.div>
            <div
              className={clsx([
                "items-start",
                "flex",
                "w-full",
                "text-sm",
                "md:text-base",
                "font-medium",
                "flex-col",
                "h-full",
              ])}
            >
              <div className={clsx(["dark:text-cyan-400"])}>
                {clientProvider}
              </div>
              {address ? (
                <div
                  className={clsx([
                    "font-light",
                    "w-full",
                    "text-sm",
                    "md:text-base",
                  ])}
                >
                  {address}
                </div>
              ) : (
                <div className={clsx(["font-light", "w-full"])}>
                  Sin dirección física. Puedes agregarla editando el perfil.{" "}
                  <Link
                    to={`/settings/sources/${clientProvider}`}
                    className={clsx(["font-medium", "hover:underline"])}
                  >
                    ¿Deseas agregarla ahora?
                  </Link>
                </div>
              )}
            </div>
          </div>
          {contactData?.length ? (
            <SourcesContactCards items={contactData} />
          ) : (
            <div className={clsx(["md:text-base", "text-sm", "w-full"])}>
              Aún no has registrado información de contacto. Puedes agregar
              números de teléfono, correos electrónicos, enlaces a aplicaciones
              de mensajería, etc.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("shared.close"),
            icon: <MdClose size={ICON_SIZES.MD} />,
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
          },
        ]}
        leftContent={
          <ButtonGroup
            align="left"
            buttons={[
              {
                tooltip: t("shared.share"),
                icon: <MdOutlineShare size={ICON_SIZES.MD} />,
                onClick: async (e: any) => {
                  e?.preventDefault();
                  await navigator
                    .share({
                      text: [
                        clientProvider,
                        contactData
                          ?.map?.((info: any) => {
                            return `${info.name || "Sin nombre"}: ${
                              info.value
                            }`;
                          })
                          ?.join?.("\n"),
                      ].join("\n"),
                    })
                    .then((a) => {})
                    .catch((e) => {});
                },
                kind: EButtonKind.NORMAL,
                disabled: !contactData?.length,
              },
              {
                tooltip: t("shared.edit"),
                icon: <MdEdit size={ICON_SIZES.MD} />,
                onClick: () => navigate(`/settings/sources/${clientProvider}`),
                kind: EButtonKind.EDIT,
              },
            ]}
          />
        }
      />
    </Modal>
  );
};

export default ClientProviderInfoModal;
